import { ActivitySpinnerWidget, ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, DialogWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { callGetTaskViaApiService, GeocodingTaskResponse } from '~src/geocoding/requests/GeocodingBackgrounderRequests';
import { callDeleteConfigViaApiService, callGetConfigsViaApiService, GeocodingConfig, GeocodingConfigsResponse } from '~src/geocoding/requests/GeocodingConfigRequests';
import { callGetColumnsViaApiService, GetDatasourceColumnsResponse } from '~src/geocoding/requests/GetDatasourceColumns';
import { GeocodingResponseHelper } from '~src/geocoding/utils/GeocodingResponseHelper';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { ReactModalService } from '../../../commonInfra/dialogs/ReactModalService';
import { launchGeocodingConfigDialog } from '../configDialog/GeocodingConfigDialog';
import { GeocodingRecordDataGrid, GeocodingRecordItem } from './GeocodingRecordDataGrid';

export interface GeocodingRecordDialogProps {
  datasourceName: string;
  datasourceLuid: string;
  datasourceId: string;
  datasource: IContentInfo<'datasource'>;
  close: () => void;
}

/**
 * Options used for ReactModalService
 */
export function launchGeocodingRecordDialog(props: Omit<GeocodingRecordDialogProps, 'close'>): void {
  ReactModalService.openCustom(closeAndDismiss => {
    const dialogProps = ReactModalService.getDefaultDialogProps({
      type: DialogType.NormalWithTitleBar,
      title: Messages.geocodingDialogTitle({DS_NAME: props.datasourceName}),
      dialogWidth: TabDialogWidth.width900px,
      shouldNotCloseOnGlassClick: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      showCloseButton: true,
      testId: 'geocoding-record-dialog'
    }, closeAndDismiss);

    return (
      <DialogWidget {...dialogProps}>
        <GeocodingRecordDialog
          datasourceName={props.datasourceName}
          datasourceLuid={props.datasourceLuid}
          datasourceId={props.datasourceId}
          datasource={props.datasource}
          close={closeAndDismiss.close}
        />
      </DialogWidget>
    );
  });
}

interface GeocodingRecordDialogState {
  configRecords: GeocodingRecordItem[];
  isLoading: boolean;
}


export class GeocodingRecordDialog extends React.Component<GeocodingRecordDialogProps, GeocodingRecordDialogState> {
  public constructor(props: GeocodingRecordDialogProps) {
    super(props);
    this.state = {
      configRecords: [],
      isLoading: true
    };
    this.getGeocodingConfigsForDatasource(this.props.datasourceLuid);
  }

    private renderFooterButton(): JSX.Element {
      return (
        <div className={'geocodingFooterButtonContainer'}>
          <ButtonWidget buttonType={ButtonType.Primary} onClick={this.props.close}>
            {Messages.geocodingDialogOkButton()}
          </ButtonWidget>
        </div>
      );
    }

    private getGeocodingConfigsForDatasource(datasourceLuid: string): Promise<void> {
      return callGetConfigsViaApiService({datasourceLuid})
      .then(
        (configsResult: GeocodingConfigsResponse) => {
          const configs: GeocodingConfig[] = GeocodingResponseHelper.processGetGeocodingConfigsForDatasourceResponse(configsResult);
          this.getGeocodingTasksForConfigs(configs);
          return Promise.resolve();
        },
        () => {
          return Promise.reject();
        }
      );
    }

    private getGeocodingTasksForConfigs(configs: GeocodingConfig[])
    {
      const promises = configs
        .filter(config => config.runGeocodingTask !== undefined)
        .map(config => this.getTask(config.runGeocodingTask));
      if (promises.length > 0) {
        Promise.all(promises).then(
          (taskResults: GeocodingTaskResponse[]) => {
            this.setState({ configRecords: GeocodingResponseHelper.getConfigRecords(configs, taskResults), isLoading: false });
            Promise.resolve();
          },
          () => {
            Promise.reject();
          }
        );
      } else {
        this.setState({ configRecords: GeocodingResponseHelper.getConfigRecords(configs, []), isLoading: false });
        return Promise.resolve();
      }
    }

    private getTask(taskLuid: string): Promise<GeocodingTaskResponse> {
      return callGetTaskViaApiService({taskLuid});
    }

    private handleUpdateRecord = () => {
      this.setState({isLoading: true});
      this.getGeocodingConfigsForDatasource(this.props.datasourceLuid);
    };

    private handleNewEditConfig = (configRecord?: GeocodingRecordItem) => {
      callGetColumnsViaApiService({datasourceLuid: this.props.datasourceLuid})
      .then(
        (result: GetDatasourceColumnsResponse) => {
          const { columns, colNameToCaptionMap } = GeocodingResponseHelper.processGetDatasourceColumnsResponse(result);
          launchGeocodingConfigDialog({
            datasourceName: this.props.datasourceName,
            datasourceLuid: this.props.datasourceLuid,
            datasourceId: this.props.datasourceId,
            datasource: this.props.datasource,
            geocodingConfig: configRecord?.config,
            geocodingTask: configRecord?.task,
            columns,
            colNameToCaptionMap,
            updateRecord: this.handleUpdateRecord});
          return Promise.resolve();
        },
        (reason: GetDatasourceColumnsResponse) => {
          // TODO: show or handle error when getColumns fails
          return Promise.reject(reason);
        }
      );
    };

    private handleDeleteConfig = (configId: string) => {
      callDeleteConfigViaApiService(
        {
          datasourceLuid: this.props.datasourceLuid,
          configId: configId
        })
      .then(
        () => {
          this.handleUpdateRecord();
          return Promise.resolve();
        },
        () => {
          return Promise.reject();
        }
      );
    };

    private buildNewGeocodeButton(): React.ReactNode {
      return (
        <ButtonWidget className='newGeocodeButton' testId='geocoding-record-create-new' buttonType={ButtonType.Outline} onClick={() => this.handleNewEditConfig()}>
          {Messages.geocodingRecordDialogNewButton()}
        </ButtonWidget>
      );
    }

    private buildRecordDataGridArea(): React.ReactNode {
      if (this.state.configRecords.length === 0) {
        return <div>{Messages.geocodingRecordEmptyDatagrid()}</div>;
      }
      return (
        <>
          <div>{Messages.geocodingRecordDialogContentInfo()}</div>
          <GeocodingRecordDataGrid configRecords={this.state.configRecords} handleEditConfig={this.handleNewEditConfig} handleDeleteConfig={this.handleDeleteConfig}/>
        </>
      );
    }

    private spinner(): JSX.Element {
      const styleProps: React.HTMLAttributes<HTMLDivElement> = {
        style: { display: 'flex', justifyContent: 'center' },
      };
      return (
        <div className='geocoding-data-grid-spinner-wrapper' {...styleProps}>
          <ActivitySpinnerWidget testId='geocoding-data-grid-spinner' shouldShowUnderlay={false} />
        </div>
      );
    }

    private getMainContent(): JSX.Element {
      return (
        <>
          {this.buildRecordDataGridArea()}
          {this.buildNewGeocodeButton()}
          {this.renderFooterButton()}
        </>
      );
    }

    public render(): JSX.Element {
      return (
        <div>
          <div className='geocodingLargeText'>{Messages.geocodingRecordDialogContentHeader()}</div>
          {
            this.state.isLoading ? this.spinner() : this.getMainContent()
          }
        </div>
      );
    }
  }
